/**
 * Browser print and download for manual payment / invoice detail (same payload as check view sheet).
 *
 * Currency and short dates follow the same conventions as payment receipt PDFs (en-US, USD).
 */

import {
  type RenewalFeesPayload,
  formatPaymentCalendarUsDate,
  formatPaymentPeriodUsRangeLabel,
} from "@/components/license-details/cards/license-payments-derived";
import type {
  ManualCheckPaymentDetail,
  ManualCheckPaymentLicenseDetail,
} from "@/hera-api/manual-check-payments-api";
import { formatCurrency, normalizeCalendarDateString } from "@/lib/utils";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function parseNum(s: string): number {
  const n = Number.parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

/** US short date (MM/DD/YYYY) for API calendar dates and UTC instants. */
export function formatPaymentDocumentUsDate(
  value: string | null | undefined,
): string {
  if (value == null || value === "") {
    return "";
  }
  const calendar = normalizeCalendarDateString(value);
  if (calendar && value.trim().startsWith(calendar)) {
    return formatPaymentCalendarUsDate(calendar);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

export type RenewalFeesByLicenseId =
  | Map<string, RenewalFeesPayload>
  | Record<string, RenewalFeesPayload | undefined>;

function feesForLicense(
  map: RenewalFeesByLicenseId | undefined,
  licenseId: string,
): RenewalFeesPayload {
  if (!map) return undefined;
  if (map instanceof Map) return map.get(licenseId);
  return map[licenseId];
}

export type ManualPaymentDetailPrintOptions = {
  variant: ManualPaymentPrintVariant;
  renewalFeesByLicenseId?: RenewalFeesByLicenseId;
};

export type ManualPaymentPrintVariant = "invoice" | "check" | "processor";

function titleForVariant(variant: ManualPaymentPrintVariant): string {
  return variant === "invoice"
    ? "Invoice"
    : variant === "processor"
      ? "Payment"
      : "Check";
}

/** @internal Exported for unit tests */
export function buildDetailBodyHtml(
  detail: ManualCheckPaymentDetail,
  variant: ManualPaymentPrintVariant,
  options?: { renewalFeesByLicenseId?: RenewalFeesByLicenseId },
): string {
  const processingFeeByLicense: Record<string, number> = {};
  for (const lic of detail.licenses) {
    let sum = 0;
    for (const p of lic.payments) {
      sum += parseNum(p.processing_fee);
    }
    processingFeeByLicense[lic.license_id] = sum;
  }

  let inspectionTotal = 0;
  for (const lic of detail.licenses) {
    if (lic.inspection_payment) {
      inspectionTotal += parseNum(lic.inspection_payment.fee_paid);
    }
  }

  const reg = parseNum(detail.total_registration_fee);
  const late = parseNum(detail.total_late_fee);
  const proc = parseNum(detail.total_processing_fee);
  /** Invoice MT lines never carry processing fees; omit zero rows so totals match receipts. */
  const hideProcessingInDocument = variant === "invoice" && proc === 0;
  const total =
    reg + late + inspectionTotal + (hideProcessingInDocument ? 0 : proc);

  const idLabel =
    variant === "invoice"
      ? "Invoice ID"
      : variant === "processor"
        ? "Payment reference"
        : "Check ID";

  const licensesHtml = detail.licenses
    .map((lic: ManualCheckPaymentLicenseDetail) => {
      const periodsHtml = lic.payments
        .map((p, periodIndex) => {
          const periodLabel = escapeHtml(
            formatPaymentPeriodUsRangeLabel(
              p.period_start_date,
              p.period_end_date,
              feesForLicense(options?.renewalFeesByLicenseId, lic.license_id),
            ),
          );
          const showInspection =
            periodIndex === 0 && lic.inspection_payment != null;
          return `
          <section class="license-block">
            <h4>${periodLabel}</h4>
            <table class="fee-table">
              <thead>
                <tr><th scope="col">Fee type</th><th scope="col" class="num">Amount</th></tr>
              </thead>
              <tbody>
              <tr><td>Registration</td><td class="num">${escapeHtml(formatCurrency(p.fee_paid))}</td></tr>
              <tr><td>Late fee</td><td class="num">${escapeHtml(formatCurrency(p.late_fee_paid))}</td></tr>
              ${
                showInspection && lic.inspection_payment
                  ? `<tr><td>Inspection fee</td><td class="num">${escapeHtml(formatCurrency(lic.inspection_payment.fee_paid))}</td></tr>`
                  : ""
              }
              </tbody>
            </table>
          </section>`;
        })
        .join("");

      const procFeeNum = processingFeeByLicense[lic.license_id] ?? 0;
      const procLine =
        variant === "invoice"
          ? procFeeNum > 0
            ? `<p class="proc-line"><strong>Processing fee (this registration):</strong> ${escapeHtml(formatCurrency(procFeeNum))}</p>`
            : ""
          : `<p class="proc-line"><strong>Processing fee (this registration):</strong> ${escapeHtml(formatCurrency(procFeeNum))}</p>`;

      return `
      <article class="license">
        <h3 class="license-title">${escapeHtml(lic.license_display_id)}</h3>
        <table class="meta">
          ${
            lic.property_address
              ? `<tr><th>Address</th><td>${escapeHtml(lic.property_address)}</td></tr>`
              : ""
          }
          <tr><th>Jurisdiction</th><td>${escapeHtml(lic.jurisdiction_name)}</td></tr>
          <tr><th>License type</th><td>${escapeHtml(lic.license_type_name)}</td></tr>
        </table>
        ${periodsHtml}
        ${procLine}
      </article>`;
    })
    .join("");

  const showCheckNumberRow =
    variant !== "invoice" &&
    variant !== "processor" &&
    Boolean(detail.check_number);
  const showPaymentReferenceRow =
    variant === "invoice" && Boolean(detail.check_number);

  const createdFmt = formatPaymentDocumentUsDate(detail.created_at);
  const paymentFmt = detail.payment_date
    ? formatPaymentDocumentUsDate(detail.payment_date)
    : "";

  const processingSummaryRow = hideProcessingInDocument
    ? ""
    : `<tr><td>Processing fees</td><td class="num">${escapeHtml(formatCurrency(proc))}</td></tr>`;

  const mailingLabel =
    variant === "invoice" ? "Remittance / mailing address" : "Mailing address";

  return `
  <header class="doc-header">
    <table class="meta">
      <tr><th>${idLabel}</th><td>${escapeHtml(detail.check_id)}</td></tr>
      <tr><th>Status</th><td>${escapeHtml(detail.status)}</td></tr>
      ${
        showCheckNumberRow && detail.check_number
          ? `<tr><th>Check number</th><td>${escapeHtml(detail.check_number)}</td></tr>`
          : ""
      }
      ${
        showPaymentReferenceRow && detail.check_number
          ? `<tr><th>Payment reference</th><td>${escapeHtml(detail.check_number)}</td></tr>`
          : ""
      }
      ${
        detail.registrant_name
          ? `<tr><th>Registrant</th><td>${escapeHtml(detail.registrant_name)}</td></tr>`
          : ""
      }
      ${
        detail.registrant_company_name
          ? `<tr><th>Registrant Company</th><td>${escapeHtml(detail.registrant_company_name)}</td></tr>`
          : ""
      }
      <tr><th>AMS</th><td>${escapeHtml(detail.ams_company_name?.trim() ? detail.ams_company_name : "—")}</td></tr>
      <tr><th>Created</th><td>${escapeHtml(createdFmt || detail.created_at)}</td></tr>
      ${
        detail.payment_date
          ? `<tr><th>Payment date</th><td>${escapeHtml(paymentFmt || detail.payment_date)}</td></tr>`
          : ""
      }
      ${
        detail.check_mailing_address
          ? `<tr><th>${escapeHtml(mailingLabel)}</th><td>${escapeHtml(detail.check_mailing_address)}</td></tr>`
          : ""
      }
    </table>
  </header>
  <h2 class="section-title">Registrations</h2>
  ${licensesHtml}
  <section class="summary">
    <h2 class="section-title">Summary</h2>
    <table class="fee-table">
      <thead>
        <tr><th scope="col">Description</th><th scope="col" class="num">Amount</th></tr>
      </thead>
      <tbody>
      <tr><td>Registration fees</td><td class="num">${escapeHtml(formatCurrency(reg))}</td></tr>
      <tr><td>Late fees</td><td class="num">${escapeHtml(formatCurrency(late))}</td></tr>
      ${
        inspectionTotal > 0
          ? `<tr><td>Inspection fees</td><td class="num">${escapeHtml(formatCurrency(inspectionTotal))}</td></tr>`
          : ""
      }
      ${processingSummaryRow}
      <tr class="total"><td><strong>Total</strong></td><td class="num"><strong>${escapeHtml(formatCurrency(total))}</strong></td></tr>
      </tbody>
    </table>
  </section>`;
}

const DOCUMENT_STYLES = `    body { font-family: system-ui, -apple-system, Segoe UI, sans-serif; margin: 24px; color: #111; font-size: 14px; }
    h1 { font-size: 1.35rem; margin: 0 0 16px; }
    h2 { font-size: 1.1rem; margin: 24px 0 12px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    h3 { font-size: 1rem; margin: 16px 0 8px; }
    h4 { font-size: 0.9rem; margin: 12px 0 6px; color: #333; }
    table.meta { width: 100%; max-width: 560px; margin-bottom: 8px; }
    table.meta th { text-align: left; vertical-align: top; padding: 4px 12px 4px 0; width: 140px; color: #444; }
    table.meta td { padding: 4px 0; }
    table.fee-table { width: 100%; max-width: 480px; border-collapse: collapse; margin: 8px 0 12px; }
    table.fee-table td { padding: 6px 8px; border: 1px solid #ddd; }
    table.fee-table td.num { text-align: right; }
    tr.total td { border-top: 2px solid #333; }
    article.license { margin-bottom: 28px; page-break-inside: avoid; }
    section.license-block { margin-left: 8px; margin-bottom: 12px; }
    p.proc-line { margin: 8px 0 0; font-size: 13px; }
    @media print {
      body { margin: 12mm; }
    }`;

function buildManualPaymentHtmlDocument(
  detail: ManualCheckPaymentDetail,
  variant: ManualPaymentPrintVariant,
  withAutoPrint: boolean,
  options?: { renewalFeesByLicenseId?: RenewalFeesByLicenseId },
): string {
  const titleText = titleForVariant(variant);
  const bodyInner = buildDetailBodyHtml(detail, variant, options);
  const printScript = withAutoPrint
    ? `
  <script>
    window.onload = function () {
      window.focus();
      window.print();
    };
  </script>`
    : "";
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(titleText)} ${escapeHtml(detail.check_id)}</title>
  <style>
${DOCUMENT_STYLES}
  </style>
</head>
<body>
  ${bodyInner}${printScript}
</body>
</html>`;
}

function downloadFilenameForVariant(
  variant: ManualPaymentPrintVariant,
  checkId: string,
): string {
  const prefix =
    variant === "invoice"
      ? "invoice"
      : variant === "processor"
        ? "payment"
        : "check";
  const safe = checkId.replace(/[^\w.-]+/g, "_");
  return `${prefix}-${safe}.html`;
}

/**
 * Downloads the same print layout as an HTML file (open locally and print or save as PDF).
 */
export function downloadManualPaymentDetail(
  detail: ManualCheckPaymentDetail,
  options: ManualPaymentDetailPrintOptions,
): void {
  const html = buildManualPaymentHtmlDocument(
    detail,
    options.variant,
    false,
    options,
  );
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = downloadFilenameForVariant(options.variant, detail.check_id);
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Opens a new window with a print-friendly document and triggers the browser print dialog.
 */
export function printManualPaymentDetail(
  detail: ManualCheckPaymentDetail,
  options: ManualPaymentDetailPrintOptions,
): void {
  const html = buildManualPaymentHtmlDocument(
    detail,
    options.variant,
    true,
    options,
  );

  const win = window.open("", "_blank", "noopener,noreferrer");
  if (!win) {
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
